import * as THREE from 'three';
import { ownFigureMaterials, shareFigureMaterials } from './figure-materials.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { firstUnseenEvent } from '../../../shared/network/visual-history.js';
import { SPARK_STEP_ARRIVAL } from '../combat/spark-step.js';
import { SNEAK_REVEAL_SECONDS } from '../combat/sneak-reveal.js';

/**
 * Game seconds between snapshots. A figure's displayed position and presence change across the interval before the
 * snapshot that reports why, so cues that move or reveal a figure start that much earlier to begin with the change on screen.
 */
export const PRESENCE_LEAD = 1 / 15;
/** Game seconds a Spark Step caster takes to solidify again once the bolt has put it down. */
const REAPPEAR_SECONDS = .14;
/** Past this opacity a dissolving figure casts its shadow again (the shadow pass cannot dither it). */
const SHADOW_FROM = .7;

/** How much earlier than its snapshot a cue begins: only the cues that hide, move or reveal a figure lead. */
export function presenceLead(event: Pick<CombatVisualEvent, 'kind' | 'abilityId'>) {
  return event.kind === 'teleport' || (event.kind === 'condition' && event.abilityId === 'sneak') ? PRESENCE_LEAD : 0;
}

/** Opacity is `before` until `start`, nothing until `shown`, then rises to solid at `solid`. */
interface Presence { start: number; before: number; shown: number; solid: number; met?: boolean }
interface Dissolve { figure: THREE.Object3D; surfaces: { material: THREE.Material; opacity: number; alphaHash: boolean; dither: boolean }[]; shadows: THREE.Object3D[] }

/**
 * Presentation only: hides and dissolves unit figures for cues that move or reveal them (a Spark Step's bolt carries
 * its caster; a Sneak Assassin steps out of a smoke bomb). It sets only the figure's own visibility and material opacity,
 * so the unit group's culling and fog still decide whether the unit is drawn at all.
 */
export class FigurePresence {
  private entries = new Map<string, Presence>();
  private dissolves = new Map<string, Dissolve>();
  private hidden = new Map<string, THREE.Object3D>();
  private lastId = 0;
  private revision = -1;
  private initialized = false;

  receive(snapshot: Pick<WorldSnapshot, 'revision' | 'gameTime' | 'visualEvents'>) {
    if (this.revision !== snapshot.revision) { this.clear(); this.revision = snapshot.revision; this.initialized = false; this.lastId = 0; }
    if (!this.initialized) { this.lastId = firstUnseenEvent(snapshot); this.initialized = true; }
    for (const event of snapshot.visualEvents) {
      if (event.id <= this.lastId) continue;
      this.lastId = event.id; this.cue(event, snapshot.gameTime - presenceLead(event));
    }
  }
  /** Starts the figure's part of a cue at game time `start` (also used by authoring previews). */
  cue(event: Pick<CombatVisualEvent, 'kind' | 'abilityId' | 'actorId'>, start: number) {
    if (event.kind === 'teleport') this.entries.set(event.actorId, { start, before: 1, shown: start + SPARK_STEP_ARRIVAL, solid: start + SPARK_STEP_ARRIVAL + REAPPEAR_SECONDS });
    // A Sneak Assassin stays unseen for a moment inside its smoke bomb, then stands there whole as the smoke thins.
    else if (event.kind === 'condition' && event.abilityId === 'sneak') this.entries.set(event.actorId, { start, before: 0, shown: start + SNEAK_REVEAL_SECONDS, solid: start + SNEAK_REVEAL_SECONDS });
  }
  /** The figure's opacity at `gameTime`, or undefined when no cue holds it. */
  opacity(id: string, gameTime: number) {
    const entry = this.entries.get(id);
    if (!entry) return undefined;
    if (gameTime < entry.start) return entry.before;
    if (gameTime < entry.shown) return 0;
    return THREE.MathUtils.smoothstep(gameTime, entry.shown, entry.solid);
  }
  /** Applies every held cue to its figure at the buffered game time; finished cues give the figure back untouched. */
  update(gameTime: number, figures: ReadonlyMap<string, THREE.Object3D>) {
    for (const [id, entry] of this.entries) {
      const figure = figures.get(id);
      if (this.hidden.has(id) && this.hidden.get(id) !== figure) this.release(id);
      if (this.dissolves.has(id) && this.dissolves.get(id)!.figure !== figure) this.release(id);
      // A revealed unit's figure is often still being built when its cue arrives: wait for it, then play the reveal from then.
      if (!figure) { if (gameTime > entry.solid + 3) this.entries.delete(id); continue; }
      if (!entry.met) {
        entry.met = true;
        const late = entry.before === 0 ? Math.max(0, gameTime - entry.start) : 0;
        entry.start += late; entry.shown += late; entry.solid += late;
      }
      // A finished cue, or one far ahead of a world that was rewound under it, ends.
      if (gameTime >= entry.solid || gameTime < entry.start - 2) { this.release(id); this.entries.delete(id); continue; }
      const opacity = this.opacity(id, gameTime)!;
      figure.visible = opacity > 0;
      if (opacity > 0) this.hidden.delete(id); else this.hidden.set(id, figure);
      if (opacity > 0 && opacity < 1) this.dissolve(id, figure, opacity);
      else this.restore(id);
    }
  }
  /** Gives every figure back as it was. */
  clear() {
    for (const id of new Set([...this.hidden.keys(), ...this.dissolves.keys()])) this.release(id);
    this.entries.clear();
  }
  private release(id: string) {
    const figure = this.hidden.get(id); if (figure) figure.visible = true;
    this.hidden.delete(id); this.restore(id);
  }
  /** Opaque surfaces dither (alpha hash), which needs no depth sorting and reads as a shimmer; translucent ones just fade. */
  private dissolve(id: string, figure: THREE.Object3D, opacity: number) {
    let dissolve = this.dissolves.get(id);
    if (!dissolve) {
      dissolve = { figure, surfaces: [], shadows: [] };
      ownFigureMaterials(figure);
      const seen = new Set<THREE.Material>();
      figure.traverse(node => {
        if (!(node instanceof THREE.Mesh)) return;
        if (node.castShadow) { node.castShadow = false; dissolve!.shadows.push(node); }
        for (const material of [node.material as THREE.Material | THREE.Material[]].flat()) {
          if (seen.has(material) || material instanceof THREE.ShaderMaterial) continue;
          seen.add(material);
          const dither = !material.transparent && !material.alphaHash;
          dissolve!.surfaces.push({ material, opacity: material.opacity, alphaHash: material.alphaHash, dither });
          if (dither) { material.alphaHash = true; material.needsUpdate = true; }
        }
      });
      this.dissolves.set(id, dissolve);
    }
    for (const surface of dissolve.surfaces) surface.material.opacity = surface.opacity * opacity;
    for (const node of dissolve.shadows) node.castShadow = opacity >= SHADOW_FROM;
  }
  private restore(id: string) {
    const dissolve = this.dissolves.get(id); if (!dissolve) return;
    for (const { material, opacity, alphaHash, dither } of dissolve.surfaces) {
      material.opacity = opacity;
      if (dither) { material.alphaHash = alphaHash; material.needsUpdate = true; }
    }
    for (const node of dissolve.shadows) node.castShadow = true;
    shareFigureMaterials(dissolve.figure);
    this.dissolves.delete(id);
  }
}
